import type { ReactNode } from 'react';
import { BROWSE_STAGE_LABELS, FINAL_BROWSE_STAGES, FINAL_DECISION_WINDOW_DAYS, IN_PROGRESS_BROWSE_STAGES, type BrowseStage, type BrowseStageTile } from '@nest-recruit/shared';
import { Skeleton } from '../../../../components/Skeleton';
import { toneClass } from '../../../../components/Tone';
import { formatNumber } from '../../../../lib/format';
import { useStageCounts } from '../../api/candidate-browse-api';
import { BROWSE_STAGE_TONES } from './list-format';
import styles from './StageTiles.module.css';

interface StageTilesProps {
  readonly selected: readonly string[];
  readonly onPick: (stage: BrowseStage) => void;
}

/** CAND-01's two tile cards: where everyone in progress is, and the last 30 days' final decisions. */
export function StageTiles({ selected, onPick }: StageTilesProps): ReactNode {
  const counts = useStageCounts();
  const tileOf = (stage: BrowseStage, tiles: readonly BrowseStageTile[] | undefined): BrowseStageTile | null => tiles?.find((tile) => tile.stage === stage) ?? null;

  return (
    <div className={styles.cards}>
      <section className={`${styles.card} ${styles.progress}`} aria-label="In progress by stage">
        <div className={styles.head}>
          <h2 className={styles.title}>In progress, by stage</h2>
          <span className={styles.hint}>Click a stage to show only those people</span>
        </div>
        <div className={styles.tiles}>
          {IN_PROGRESS_BROWSE_STAGES.map((stage) => (
            <Tile key={stage} stage={stage} tile={tileOf(stage, counts.data?.inProgress)} isLoading={!counts.data} isOn={selected.includes(stage)} onPick={onPick} />
          ))}
        </div>
      </section>
      <section className={`${styles.card} ${styles.finals}`} aria-label="Final decisions">
        <div className={styles.head}>
          <h2 className={styles.title}>Final decision</h2>
        </div>
        <div className={styles.tiles}>
          {FINAL_BROWSE_STAGES.map((stage) => (
            <Tile key={stage} stage={stage} tile={tileOf(stage, counts.data?.finals)} isLoading={!counts.data} isOn={selected.includes(stage)} onPick={onPick} isFinal />
          ))}
        </div>
      </section>
    </div>
  );
}

interface TileProps {
  readonly stage: BrowseStage;
  readonly tile: BrowseStageTile | null;
  readonly isLoading: boolean;
  readonly isOn: boolean;
  readonly isFinal?: boolean;
  readonly onPick: (stage: BrowseStage) => void;
}

function Tile({ stage, tile, isLoading, isOn, isFinal = false, onPick }: TileProps): ReactNode {
  const meta = isFinal ? `in the last ${FINAL_DECISION_WINDOW_DAYS} days` : averageText(tile?.averageDaysHere ?? null);
  return (
    <button type="button" className={`${toneClass} ${styles.tile}`} data-tone={BROWSE_STAGE_TONES[stage]} data-on={isOn || undefined} aria-pressed={isOn} onClick={() => onPick(stage)}>
      <span className={styles.label}>
        <span className={styles.dot} aria-hidden />
        {BROWSE_STAGE_LABELS[stage]}
      </span>
      <span className={styles.count}>{isLoading ? <Skeleton width="50%" /> : formatNumber(tile?.count ?? 0)}</span>
      {meta !== null && <span className={styles.meta}>{meta}</span>}
    </button>
  );
}

/** "avg 3 days here" only while Time figures is on — the server sends null otherwise (§L34). */
function averageText(days: number | null): string | null {
  if (days === null) return null;
  const rounded = Math.round(days);
  return `avg ${formatNumber(rounded)} ${rounded === 1 ? 'day' : 'days'} here`;
}
